import { Effect, Equal, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import {
	editEntry,
	getEntry,
	getEntryHistory,
} from "../../api/commands/entries";
import { getRecord } from "../../api/commands/records";
import type { AttributeSchemaEntry } from "../../api/schemas/attributeSchema";
import type { Entry } from "../../api/schemas/entries";
import type { RecordSummary } from "../../api/schemas/records";
import { Route, asOfOf, toHref } from "../../app/routes";
import { i18next } from "../../i18n";
import * as Attributes from "../../widgets/attributes";
import * as Editor from "../../widgets/editor";
import { defineDownload } from "../../widgets/fileDownload";
import * as Tags from "../../widgets/tags";
import { nextCursor, settlePage } from "../history";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { type EditForm, type Model, initial } from "./model";

// COMMANDS

const nullable = (asOf: Option.Option<string>) => Option.getOrNull(asOf);

export const FetchEntry = getEntry.command(
	Message.CompletedFetchEntry,
	({ knotId, asOf, generation, result }) =>
		Message.CompletedFetchEntry({
			knotId,
			asOf: Option.fromNullishOr(asOf),
			generation,
			result,
		}),
);

/** v1 `useRecordsByIds`: one `getRecord` per unique id; the ones that resolve are shown. */
export const FetchLinkedRecords = Command.define("FetchLinkedRecords", {
	args: {
		knotId: S.String,
		ids: S.Array(S.String),
		asOf: S.NullOr(S.String),
		generation: S.Number,
	},
	messages: [Message.CompletedFetchLinkedRecords],
	execute: ({ knotId, ids, asOf, generation }) =>
		Effect.forEach(
			Array.from(new Set(ids.filter((id) => id.trim() !== ""))),
			(nodeId) => getRecord.run({ nodeId, asOf }),
		).pipe(
			Effect.map((results) =>
				Message.CompletedFetchLinkedRecords({
					knotId,
					generation,
					result: Result.succeed(
						results.flatMap((r) =>
							Result.match(r, {
								onSuccess: (record) => [record as RecordSummary],
								onFailure: () => [],
							}),
						),
					),
				}),
			),
		),
});

export const FetchHistory = getEntryHistory.command(
	Message.CompletedFetchHistory,
	({ knotId, cursor, generation, result }) =>
		Message.CompletedFetchHistory({ knotId, cursor, generation, result }),
);

export const EditEntry = editEntry.command(
	Message.CompletedEditEntry,
	({ knotId, generation, editing, entry, result }) =>
		Message.CompletedEditEntry({ knotId, generation, editing, entry, result }),
	{ editing: S.Boolean },
);

export const DownloadFile = defineDownload(
	Message.CompletedDownload,
	({ generation, result }) => Message.CompletedDownload({ generation, result }),
);

/** v1 `handleBack`: back when there is history, else home. */
export const NavigateBack = Command.define("NavigateBack", {
	messages: [Message.CompletedNavigate],
	execute: Effect.suspend(() =>
		window.history.length > 1 ? Navigation.back() : Navigation.pushUrl("/"),
	).pipe(Effect.as(Message.CompletedNavigate())),
});

export const NavigateTo = Command.define("NavigateToEntry", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

// PURE HELPERS (v1 Entry.tsx, attributeHelpers.ts, uploadEntry.ts)

export type Task = Readonly<{
	id: string;
	text: string;
	completed: boolean;
	createdAt: string;
}>;

/** v1 `parseTasksFromJson`: entries without a string id and text are dropped. */
export const parseTasks = (
	json: string | null | undefined,
): ReadonlyArray<Task> => {
	if (!json) return [];
	try {
		const tasks = (JSON.parse(json) as { tasks?: unknown }).tasks;
		if (!Array.isArray(tasks)) return [];
		return tasks
			.filter(
				(t): t is Record<string, unknown> =>
					typeof t === "object" && t !== null,
			)
			.filter((t) => typeof t.id === "string" && typeof t.text === "string")
			.map((t) => ({
				id: t.id as string,
				text: t.text as string,
				completed: Boolean(t.completed),
				createdAt: typeof t.createdAt === "string" ? t.createdAt : "",
			}));
	} catch {
		return [];
	}
};

export type UploadFile = Readonly<{
	id: string;
	name: string;
	size: number;
	uploadedAt: string;
}>;

/** v1 `parseUploadEntryPayload` + `selectLatestUploadFile`: only the last well-formed file. */
export const latestUploadFile = (
	json: string | null | undefined,
): UploadFile | undefined => {
	if (!json) return undefined;
	try {
		const files = (JSON.parse(json) as { files?: unknown }).files;
		if (!Array.isArray(files)) return undefined;
		const valid = files.filter(
			(f): f is UploadFile =>
				typeof f === "object" &&
				f !== null &&
				typeof (f as UploadFile).id === "string" &&
				typeof (f as UploadFile).name === "string" &&
				Number.isFinite((f as UploadFile).size) &&
				typeof (f as UploadFile).uploadedAt === "string",
		);
		return valid[valid.length - 1];
	} catch {
		return undefined;
	}
};

export const RESERVED_ATTRIBUTE_KEYS = Attributes.RESERVED_KEYS;
export const { valueRules, buildRows, flattenRows } = Attributes;

/** The schema entries the Entry page renders: `entityType === "entry"`. */
export const entrySchema = (
	shared: Shared,
): ReadonlyArray<AttributeSchemaEntry> => Attributes.schemaFor(shared, "entry");

export const tagSuggestions = (shared: Shared): ReadonlyArray<string> =>
	Tags.suggestionsFrom(
		AsyncData.getOrElse(shared.tags, () => ({ data: [] })).data,
	);

/** v1's three error texts; `undefined` when the entry is not in a failed state. */
export const errorText = (model: Model, shared: Shared): string | undefined => {
	if (!AsyncData.isFailure(model.entry)) return undefined;
	if (model.entry.error._tag !== "NotFound")
		return i18next.t("entry.failedToLoad");
	return Option.match(shared.asOf, {
		onNone: () => i18next.t("entry.notFound"),
		onSome: (asOf) =>
			i18next.t("entry.notFoundAtDate", {
				date: new Date(asOf).toLocaleDateString(),
			}),
	});
};

// STEPS

type Return = PageReturn<Model, Message>;
type Step = (model: Model) => Return;

const fetchEntry = (model: Model, shared: Shared): Return => ({
	model: evo(model, { entry: start, edit: () => Option.none() }),
	commands: [
		FetchEntry({
			knotId: model.knotId,
			asOf: nullable(shared.asOf),
			generation: shared.generation,
		}),
	],
});

const fetchHistory = (
	model: Model,
	cursor: number,
	shared: Shared,
): Return => ({
	model: evo(model, { history: start }),
	commands: [
		FetchHistory({
			knotId: model.knotId,
			cursor,
			generation: shared.generation,
		}),
	],
});

const loadedEntry = (model: Model): Entry | null =>
	AsyncData.getOrElse(model.entry, () => null);

const editForm = (model: Model, f: (form: EditForm) => EditForm): Model =>
	evo(model, { edit: (edit) => Option.map(edit, f) });

const withAttributes = (
	model: Model,
	message: Attributes.Message,
	shared: Shared,
): Model =>
	editForm(model, (form) => ({
		...form,
		attributes: Attributes.update(
			form.attributes,
			message,
			entrySchema(shared),
		),
	}));

const foldTags = Update.foldChild({
	update: (child: Tags.Model, message: Tags.Message) =>
		Tags.update(child, message),
	read: (model: Model) => Option.map(model.edit, (form) => form.tagBox),
	write: (model: Model, tagBox: Tags.Model) =>
		editForm(model, (form) => ({ ...form, tagBox })),
	toParentMessage: (message: Tags.Message) =>
		Message.GotTagsMessage({ message }),
	foldOutMessage:
		(out: Tags.OutMessage) =>
		(model: Model): Return =>
			Tags.OutMessage.match(out, {
				// v1 `onAdd`: invalid labels show the rule; duplicates are allowed here and deduped on save.
				Selected: ({ value }) => {
					const tag = Tags.sanitizeTag(value);
					return {
						model: editForm(model, (form) =>
							tag === undefined
								? {
										...form,
										tagError: Option.some(Tags.TAG_VALIDATION_MESSAGE),
									}
								: {
										...form,
										tagError: Option.none(),
										tags: [...form.tags, tag],
									},
						),
					};
				},
				ClearedSelection: () => ({ model }),
			}),
});

const editEntryWith = (
	model: Model,
	entry: Entry,
	editing: boolean,
	shared: Shared,
): Return => ({
	model: evo(model, { saving: () => true }),
	commands: [
		EditEntry({
			knotId: entry.id,
			entry,
			editing,
			generation: shared.generation,
		}),
	],
});

/** v1 `saveEdits`: the type is always a tag; empty attribute values are dropped. */
const save = (
	model: Model,
	form: EditForm,
	entry: Entry,
	shared: Shared,
): Return => {
	const attributes = flattenRows(form.attributes);
	return editEntryWith(
		model,
		{
			...entry,
			title: form.title,
			description: form.description,
			tags: Array.from(new Set([entry.type, ...form.tags])),
			timestamp: form.timestamp,
			...(attributes ? { attributes } : {}),
		},
		true,
		shared,
	);
};

/** v1 `handleTaskToggle`: re-serialize the whole payload with the flipped task. */
const toggleTask = (
	model: Model,
	entry: Entry,
	taskId: string,
	shared: Shared,
): Return => {
	let base: Record<string, unknown> = {};
	try {
		base = entry.json?.trim()
			? (JSON.parse(entry.json) as Record<string, unknown>)
			: {};
	} catch {
		base = {};
	}
	const tasks = parseTasks(entry.json).map((task) =>
		task.id === taskId ? { ...task, completed: !task.completed } : task,
	);
	return editEntryWith(
		model,
		{ ...entry, json: JSON.stringify({ ...base, tasks }) },
		false,
		shared,
	);
};

// PAGE

export const page: Page<Model, Message> = {
	init: (route, shared) =>
		fetchEntry(initial(route._tag === "Entry" ? route.id : ""), shared),
	changedRoute: (model, previous, next, shared) => {
		const nextId = next._tag === "Entry" ? next.id : model.knotId;
		if (nextId !== model.knotId) return fetchEntry(initial(nextId), shared);
		return Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: fetchEntry(model, shared);
	},
	// A completion tagged with another entry's id is stale: the route moved on.
	update: (model, message, shared) =>
		"knotId" in message && message.knotId !== model.knotId
			? { model }
			: updateOwn(model, message, shared),
};

const updateOwn = (model: Model, message: Message, shared: Shared): Return =>
	Message.match<Return>(message, {
		CompletedFetchEntry: ({ asOf, generation, result }) => {
			if (
				generation !== shared.generation ||
				!Equal.equals(asOf, shared.asOf)
			) {
				return { model };
			}
			const settled = evo(model, { entry: (entry) => settle(entry, result) });
			return Result.match(result, {
				onSuccess: (entry) =>
					entry.nodeIds.length === 0
						? {
								model: evo(settled, {
									linkedRecords: () => AsyncData.succeed([]),
								}),
							}
						: {
								model: evo(settled, { linkedRecords: start }),
								commands: [
									FetchLinkedRecords({
										knotId: model.knotId,
										ids: entry.nodeIds,
										asOf: nullable(shared.asOf),
										generation: shared.generation,
									}),
								],
							},
				onFailure: () => ({ model: settled }),
			});
		},
		CompletedFetchLinkedRecords: ({ generation, result }) => ({
			model:
				generation === shared.generation
					? evo(model, { linkedRecords: (records) => settle(records, result) })
					: model,
		}),
		ClickedBack: () => ({ model, commands: [NavigateBack()] }),
		CompletedNavigate: () => ({ model }),
		// v1 fetches history when the tab first renders and keeps it cached.
		SelectedTab: ({ tab }) => {
			const switched = evo(model, { tab: () => tab });
			return tab === "history" && AsyncData.isIdle(model.history)
				? fetchHistory(switched, 0, shared)
				: { model: switched };
		},
		CompletedFetchHistory: ({ cursor, generation, result }) => {
			if (generation !== shared.generation) return { model };
			return {
				model: evo(model, {
					history: (history) => settlePage(history, cursor, result),
				}),
			};
		},
		ClickedLoadMoreHistory: () =>
			Option.match(nextCursor(model.history), {
				onNone: () => ({ model }),
				onSome: (cursor) => fetchHistory(model, cursor, shared),
			}),
		ToggledHistoryVersion: ({ version }) => ({
			model: evo(model, {
				expandedVersions: (versions) =>
					versions.includes(version)
						? versions.filter((v) => v !== version)
						: [...versions, version],
			}),
		}),
		ClickedViewAt: ({ validFrom }) => ({
			model,
			commands: [
				NavigateTo({
					url: toHref(
						Route.Entry({ id: model.knotId, asOf: Option.some(validFrom) }),
					),
				}),
			],
		}),
		// v1 `beginEditing`: not for uploads, not while time-traveling.
		ClickedEdit: () => {
			const entry = loadedEntry(model);
			if (
				entry === null ||
				entry.type === "upload" ||
				Option.isSome(shared.asOf)
			) {
				return { model };
			}
			return {
				model: evo(model, {
					edit: () =>
						Option.some<EditForm>({
							title: entry.title,
							description: entry.description ?? "",
							tags: entry.tags
								.map((tag) => Tags.sanitizeTag(tag))
								.filter((tag): tag is string => tag !== undefined),
							tagBox: Tags.init("edit-entry-tags"),
							tagError: Option.none(),
							attributes: buildRows(entry.attributes, entrySchema(shared)),
							editorFocused: false,
							timestamp: entry.timestamp,
						}),
				}),
			};
		},
		ClickedCancelEdit: () => ({
			model: model.saving ? model : evo(model, { edit: () => Option.none() }),
		}),
		ChangedTitle: ({ value }) => ({
			model: editForm(model, (form) => ({ ...form, title: value })),
		}),
		ChangedOccurredOn: ({ value }) => ({
			model: editForm(model, (form) => ({
				...form,
				timestamp: value === "" ? form.timestamp : value,
			})),
		}),
		GotEditorMessage: ({ message }) => ({
			model: Editor.Message.match(message, {
				EditorChanged: ({ text }) =>
					editForm(model, (form) => ({ ...form, description: text })),
				EditorFocusChanged: ({ focused }) =>
					editForm(model, (form) => ({ ...form, editorFocused: focused })),
			}),
		}),
		ClickedEditorAction: ({ action }) => ({
			model,
			commands: [Editor.EditorAction({ id: model.knotId, action })],
		}),
		CompletedEditorAction: () => ({ model }),
		GotTagsMessage: ({ message }) => foldTags(model, message),
		ClickedRemoveTag: ({ tag }) => ({
			model: editForm(model, (form) => ({
				...form,
				tagError: Option.none(),
				tags: form.tags.filter((t) => t !== tag),
			})),
		}),
		ClickedAddAttribute: () => ({
			model: withAttributes(model, Attributes.Message.ClickedAdd(), shared),
		}),
		ChangedAttributeKey: ({ index, value }) => ({
			model: withAttributes(
				model,
				Attributes.Message.ChangedKey({ index, value }),
				shared,
			),
		}),
		ChangedAttributeType: ({ index, value }) => ({
			model: withAttributes(
				model,
				Attributes.Message.ChangedType({ index, value }),
				shared,
			),
		}),
		ChangedAttributeValue: ({ index, value }) => ({
			model: withAttributes(
				model,
				Attributes.Message.ChangedValue({ index, value }),
				shared,
			),
		}),
		ClickedRemoveAttribute: ({ index }) => ({
			model: withAttributes(
				model,
				Attributes.Message.ClickedRemove({ index }),
				shared,
			),
		}),
		ClickedSave: () => {
			const entry = loadedEntry(model);
			return Option.match(model.edit, {
				onNone: () => ({ model }),
				onSome: (form) =>
					model.saving || entry === null || entry.type === "upload"
						? { model }
						: save(model, form, entry, shared),
			});
		},
		CompletedEditEntry: ({ generation, editing, entry, result }) => {
			if (generation !== shared.generation) return { model };
			const settled = evo(model, { saving: () => false });
			return Result.match(result, {
				// v1: the sent entry becomes the cached one and edit mode closes.
				onSuccess: () => ({
					model: evo(settled, {
						entry: () => AsyncData.succeed(entry),
						edit: () => Option.none(),
					}),
				}),
				// v1: Save failure stays in edit mode with no visible error; a task toggle failure refetches.
				onFailure: () =>
					editing ? { model: settled } : fetchEntry(settled, shared),
			});
		},
		ClickedToggleTask: ({ taskId }) => {
			const entry = loadedEntry(model);
			return entry === null ||
				entry.type !== "tasks" ||
				model.saving ||
				Option.isSome(shared.asOf)
				? { model }
				: toggleTask(model, entry, taskId, shared);
		},
		ToggledAdditionalData: () => ({
			model: evo(model, { additionalDataOpen: (open) => !open }),
		}),
		ClickedDownload: () => {
			const entry = loadedEntry(model);
			const file = entry === null ? undefined : latestUploadFile(entry.json);
			if (
				entry === null ||
				entry.type !== "upload" ||
				!file ||
				model.downloading
			) {
				return { model };
			}
			return {
				model: evo(model, {
					downloading: () => true,
					downloadError: () => Option.none(),
				}),
				commands: [
					DownloadFile({
						knotId: entry.id,
						fileId: file.id,
						fileName: file.name,
						generation: shared.generation,
					}),
				],
			};
		},
		CompletedDownload: ({ generation, result }) => ({
			model:
				generation !== shared.generation
					? model
					: evo(model, {
							downloading: () => false,
							downloadError: () =>
								Result.isFailure(result)
									? Option.some(i18next.t("entry.downloadFailed"))
									: Option.none(),
						}),
		}),
	});
